import { useParams } from 'react-router-dom';
import { ArrowLeft, Receipt } from 'lucide-react';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';
import { type GetTransactionPaymentQuery, useGetTransactionPaymentQuery } from 'data/__generated__';

import { CopyValue } from 'components/CopyValue';
import { DetailLink, DetailValue } from 'components/DetailsView';
import { ColumnsSectionContainer, SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { PageActionsMenu, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';
import { getExtendedFormattedDatetime } from 'helpers/dateTimes';
import { getPaymentMethodLabel } from 'helpers/paymentMethods';
import { formatPrice } from 'utils/price';

import { PaymentMethodStatusTag } from 'pages/Transactions/components/PaymentMethodStatusTag';
import { StatusTag } from 'pages/Transactions/components/StatusTag';

import { getPaymentInstrumentId, getPaymentMethodTargetPath } from './components/PaymentIdLink';
import { PaymentInstrumentDetails } from './components/PaymentInstrumentDetails';
import { PaymentsSection } from './components/PaymentsSection';

export const PaymentDetails = () => {
    const { paymentId = '' } = useParams<{ paymentId: string }>();
    const { data, loading, error } = useGetTransactionPaymentQuery({ variables: { paymentId } });

    if (loading && !data) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (error) {
        throw new Error('Une erreur est survenue lors de la récupération du paiement');
    }

    const payment = data?.transactionPayment;
    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [
                { label: 'Retour à la liste', icon: <ArrowLeft />, to: PAGES.payments.url },
                ...(payment
                    ? [
                          {
                              label: 'Voir la transaction',
                              icon: <Receipt />,
                              to: `/transaction/${payment.transaction._id}`,
                          },
                      ]
                    : []),
            ],
        },
    ];

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.paymentDetails} />
                </HeaderTitle>
                <PageActionsMenu groups={actionGroups} />
            </Header>
            <Content>
                {payment ? <PaymentSections payment={payment} /> : <Message>Paiement introuvable</Message>}
            </Content>
        </Container>
    );
};

const PaymentSections = ({ payment }: { payment: NonNullable<GetTransactionPaymentQuery['transactionPayment']> }) => {
    const { _id, paymentMethod, transaction, instrument } = payment;
    const { amountUsed, explanation, name, paymentMethodName, provider, providerTransactionId, status, type } =
        paymentMethod;
    const instrumentId = getPaymentInstrumentId(paymentMethod);
    const organizationId = instrument?.organizationCard?.organization?._id;
    const instrumentPath =
        getPaymentMethodTargetPath(paymentMethod) ?? (organizationId ? `/organization/${organizationId}` : undefined);

    return (
        <>
            <SectionContainer title="Paiement" keepOpen>
                <ColumnsSectionContainer numberOfColumns={2}>
                    <SectionColumn>
                        <DetailValue label="Statut" value={<PaymentMethodStatusTag status={status} />} />
                        <DetailValue label="Montant" value={formatPrice(amountUsed)} />
                        <DetailValue label="Moyen de paiement" value={getPaymentMethodLabel(paymentMethodName)} />
                        <DetailValue label="Libellé" value={name || '-'} />
                        {provider ? (
                            <DetailValue label="Fournisseur" value={type ? `${provider} (${type})` : provider} />
                        ) : null}
                        {explanation ? <DetailValue label="Explication" value={explanation} /> : null}
                        {providerTransactionId ? (
                            <CopyValue label="Identifiant chez le prestataire" value={providerTransactionId} />
                        ) : null}
                    </SectionColumn>
                    <SectionColumn>
                        <DetailValue
                            label="Date de la transaction"
                            value={getExtendedFormattedDatetime(transaction.createdAt)}
                        />
                        <DetailLink
                            label="Transaction"
                            name={transaction.productName}
                            value={transaction._id}
                            path="transaction"
                        />
                        <DetailValue
                            label="Statut de la transaction"
                            value={<StatusTag state={transaction.newState} />}
                        />
                        <DetailLink
                            label="Utilisateur"
                            name={transaction.userFullName}
                            value={transaction.userId}
                            path="user"
                        />
                        <DetailLink label="Site" name={transaction.siteName} value={transaction.siteId} path="site" />
                    </SectionColumn>
                </ColumnsSectionContainer>
            </SectionContainer>
            <SectionContainer
                title="Moyen de paiement"
                isInitiallyOpen
                linkToMore={instrumentPath}
                linkToMoreLabel="voir sa fiche"
            >
                <ColumnsSectionContainer numberOfColumns={3}>
                    {instrument ? (
                        <PaymentInstrumentDetails instrument={instrument} />
                    ) : (
                        <Message>
                            {instrumentId
                                ? 'Moyen de paiement introuvable'
                                : "Paiement d'une intégration : aucun moyen de paiement enregistré chez Totem"}
                        </Message>
                    )}
                </ColumnsSectionContainer>
            </SectionContainer>
            {/* Un batch Stripe, une formule : une seule charge pour plusieurs transactions. */}
            {providerTransactionId ? (
                <SectionContainer title="Paiements du même prélèvement" isInitiallyOpen>
                    <SectionColumn>
                        <PaymentsSection
                            scope={{ providerTransactionIds: [providerTransactionId] }}
                            hiddenColumnIds={['userFullName']}
                            highlightedPaymentId={_id}
                            emptyMessage="Aucun autre paiement pour ce prélèvement"
                            dataTest="payment-same-charge"
                        />
                    </SectionColumn>
                </SectionContainer>
            ) : null}
            {instrumentId ? (
                <SectionContainer title="Paiements de l'utilisateur avec ce moyen" isInitiallyOpen>
                    <SectionColumn>
                        <PaymentsSection
                            scope={{ paymentMethodIds: [instrumentId], userIds: [transaction.userId] }}
                            hiddenColumnIds={['userFullName']}
                            highlightedPaymentId={_id}
                            emptyMessage="Aucun autre paiement de l'utilisateur avec ce moyen"
                            dataTest="payment-same-instrument"
                        />
                    </SectionColumn>
                </SectionContainer>
            ) : null}
        </>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    flex: 1;
    padding: 15px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Message = styled.div`
    color: ${({ theme }) => theme.infoTextColor};
`;
